/**
 * The route table.
 *
 * Feature screens are code-split; the shell is not, so navigation chrome is on
 * screen before the screen behind it has downloaded. Every leaf carries its own
 * `errorElement`, which keeps a broken screen *inside* the shell instead of
 * costing the user their navigation.
 *
 * `handle` is the route's own description of itself — the top bar reads it for
 * the title and breadcrumb. See `RouteHandle` in `components/app/Topbar`.
 */

import { lazy, Suspense } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import { AppShell, AppShellSkeleton, ScreenSkeleton } from '@/components/app/AppShell';
import { RouteError } from '@/components/app/RouteError';
import {
  AnalyticsScreenSkeleton,
  BoardScreenSkeleton,
  GettingStartedSkeleton,
  RecordDetailSkeleton,
  SettingsPaneSkeleton,
  SettingsScreenSkeleton,
} from '@/components/app/RouteSkeletons';
import { Button, EmptyState, ErrorState } from '@/components/ui';
import { ChangePasswordGate } from '@/features/auth/ChangePasswordGate';
import { LoginPage } from '@/features/auth/LoginPage';
import { InvitePage } from '@/features/auth/InvitePage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { ROUTES } from '@/lib/constants';
import { isUnauthorized, useSession } from '@/lib/queries';

/* ────────────────────────────────────────────────────────────────────────────
   Lazy screens — owned by the feature agents
   ──────────────────────────────────────────────────────────────────────────── */

const InboxScreen = lazy(() => import('@/features/inbox/InboxScreen'));
const BoardScreen = lazy(() => import('@/features/board/BoardScreen'));
const ContactsScreen = lazy(() => import('@/features/contacts/ContactsScreen'));
const ContactDetailScreen = lazy(() => import('@/features/contacts/ContactDetailScreen'));
const CompaniesScreen = lazy(() => import('@/features/companies/CompaniesScreen'));
const CompanyDetailScreen = lazy(() => import('@/features/companies/CompanyDetailScreen'));
const TasksScreen = lazy(() => import('@/features/tasks/TasksScreen'));
const AnalyticsScreen = lazy(() => import('@/features/analytics/AnalyticsScreen'));
const GettingStartedScreen = lazy(() => import('@/features/onboarding/GettingStartedScreen'));
const BugsScreen = lazy(() => import('@/features/bugs/BugsScreen'));
const SettingsLayout = lazy(() => import('@/features/settings/SettingsLayout'));
const ProfileSettings = lazy(() => import('@/features/settings/ProfileSettings'));
const TeamSettings = lazy(() => import('@/features/settings/TeamSettings'));
const PipelineSettings = lazy(() => import('@/features/settings/PipelineSettings'));
const TagsSettings = lazy(() => import('@/features/settings/TagsSettings'));
const ViewsSettings = lazy(() => import('@/features/settings/ViewsSettings'));
const AuditSettings = lazy(() => import('@/features/settings/AuditSettings'));
const NotificationSettings = lazy(() => import('@/features/settings/NotificationSettings'));
const ApiSettings = lazy(() => import('@/features/settings/ApiSettings'));
const MailSettings = lazy(() => import('@/features/settings/MailSettings'));

/* ────────────────────────────────────────────────────────────────────────────
   Suspense
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Every leaf gets its *own* boundary with its *own* skeleton. A shared spinner
 * (or a shared table skeleton) at the shell level would paint the wrong chrome
 * for the board, analytics and settings, and the swap to the real screen would
 * visibly jump.
 */
function chunk(fallback: ReactNode, screen: ReactNode): ReactElement {
  return <Suspense fallback={fallback}>{screen}</Suspense>;
}

/* ────────────────────────────────────────────────────────────────────────────
   Guard
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * While the session probe is in flight we paint the whole frame in skeleton —
 * a spinner here would flash the layout in and out on every cold load.
 *
 * Only a **401** signs anyone out. A 500 or a dropped connection on `/auth/me`
 * says nothing about the cookie, and bouncing to `/login` on one would cost the
 * user the record they were reading, their scroll position and any half-typed
 * note. Those get a retry surface instead; the session query has already tried
 * twice on its own by the time this renders.
 */
function RequireAuth() {
  const { data, isPending, isError, error, refetch, isFetching } = useSession();
  const location = useLocation();

  if (isPending) return <AppShellSkeleton />;

  const unauthorised = isError && isUnauthorized(error);

  if (unauthorised || (!isError && !data)) {
    return (
      <Navigate
        to={ROUTES.login}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!data) {
    return (
      <div className="flex h-full w-full items-center justify-center px-8 py-12">
        <div className="w-full max-w-[420px]">
          <ErrorState
            error={error}
            title="We could not confirm your session"
            description="The server did not answer the sign-in check. You are most likely still signed in — retry before signing in again."
            onRetry={() => refetch()}
            secondaryAction={
              <Button variant="ghost" onClick={() => window.location.assign(ROUTES.login)}>
                Sign in again
              </Button>
            }
            className={isFetching ? 'opacity-70' : undefined}
          />
        </div>
      </div>
    );
  }

  /* The first-login wall. Placed AFTER the session checks so an unauthenticated
     visitor still goes to /login, and BEFORE the outlet so no route — including
     one reached by typing a URL — renders while the account is still on the
     password somebody else chose. */
  if (data.mustChangePassword) return <ChangePasswordGate email={data.user.email} />;

  return <Outlet />;
}

/* ────────────────────────────────────────────────────────────────────────────
   404
   ──────────────────────────────────────────────────────────────────────────── */

function NotFound() {
  const navigate = useNavigate();
  return (
    <div className="flex h-full w-full items-center justify-center px-8 py-12">
      <div className="w-full max-w-[420px]">
        <EmptyState
          variant="search"
          title="No such page"
          description="The address does not match anything in this workspace. It may have been renamed, or the link may be truncated."
          action={
            /* Navigation, not creation — so not gold. See the gold rule in
               "Toolbar grammar", docs/UI-PRIMITIVES.md. */
            <Button variant="secondary" onClick={() => navigate(ROUTES.inbox)}>
              Back to Inbox
            </Button>
          }
        />
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   Table
   ──────────────────────────────────────────────────────────────────────────── */

const routes: RouteObject[] = [
  {
    path: ROUTES.login,
    element: <LoginPage />,
    errorElement: <RouteError />,
  },
  {
    path: ROUTES.invite,
    element: <InvitePage />,
    errorElement: <RouteError />,
  },
  {
    path: ROUTES.resetPassword,
    element: <ResetPasswordPage />,
    errorElement: <RouteError />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to={ROUTES.inbox} replace /> },

          {
            path: 'inbox',
            element: chunk(<ScreenSkeleton />, <InboxScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Inbox' },
          },
          {
            path: 'inbox/:id',
            element: chunk(<ScreenSkeleton />, <InboxScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Inbox', titled: true },
          },
          {
            path: 'board',
            element: chunk(<BoardScreenSkeleton />, <BoardScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Board' },
          },
          {
            path: 'contacts',
            element: chunk(<ScreenSkeleton />, <ContactsScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Contacts' },
          },
          {
            path: 'contacts/:id',
            element: chunk(<RecordDetailSkeleton />, <ContactDetailScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Contact', parent: { label: 'Contacts', to: ROUTES.contacts } },
          },
          {
            path: 'companies',
            element: chunk(<ScreenSkeleton />, <CompaniesScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Companies' },
          },
          {
            path: 'companies/:id',
            element: chunk(<RecordDetailSkeleton />, <CompanyDetailScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Company', parent: { label: 'Companies', to: ROUTES.companies } },
          },
          {
            path: 'tasks',
            element: chunk(<ScreenSkeleton />, <TasksScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Tasks', titled: true },
          },
          {
            path: 'analytics',
            element: chunk(<AnalyticsScreenSkeleton />, <AnalyticsScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Analytics', titled: true },
          },

          {
            /* Not nested under Settings. It is not a preference, it is the
               product's own explanation of itself, and it has to be reachable
               from a cold link without three clicks of chrome in front of it. */
            path: 'getting-started',
            element: chunk(<GettingStartedSkeleton />, <GettingStartedScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Getting started', titled: true },
          },

          {
            /* Triage for the in-app bug reporter. The screen refuses itself for
               a non-admin rather than the route doing it, so a stale link gives
               the reader a sentence instead of a silent bounce to the Inbox —
               and the server is the gate either way: every `/admin/bugs` route
               403s regardless of what the client renders. */
            path: 'bugs',
            element: chunk(<ScreenSkeleton />, <BugsScreen />),
            errorElement: <RouteError />,
            handle: { title: 'Bug reports', titled: true },
          },

          {
            path: 'settings',
            element: chunk(<SettingsScreenSkeleton />, <SettingsLayout />),
            errorElement: <RouteError />,
            handle: { title: 'Settings', titled: true },
            children: [
              { index: true, element: <Navigate to="profile" replace /> },
              {
                path: 'profile',
                element: chunk(<SettingsPaneSkeleton />, <ProfileSettings />),
                errorElement: <RouteError />,
                handle: { title: 'Profile', parent: { label: 'Settings', to: ROUTES.settings } },
              },
              {
                path: 'notifications',
                element: chunk(<SettingsPaneSkeleton />, <NotificationSettings />),
                errorElement: <RouteError />,
                handle: {
                  title: 'Notifications',
                  parent: { label: 'Settings', to: ROUTES.settings },
                },
              },
              {
                path: 'team',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <TeamSettings />),
                errorElement: <RouteError />,
                handle: { title: 'Team', parent: { label: 'Settings', to: ROUTES.settings } },
              },
              {
                path: 'pipeline',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <PipelineSettings />),
                errorElement: <RouteError />,
                handle: { title: 'Pipeline', parent: { label: 'Settings', to: ROUTES.settings } },
              },
              {
                path: 'tags',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <TagsSettings />),
                errorElement: <RouteError />,
                handle: { title: 'Tags', parent: { label: 'Settings', to: ROUTES.settings } },
              },
              {
                path: 'views',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <ViewsSettings />),
                errorElement: <RouteError />,
                // "Saved views" — the words the pane's own h1, the settings rail,
                // ⌘K's result sublabel and `CreateMenu`'s fifth verb all use. The
                // crumb was the one place in the product that said "Views".
                handle: {
                  title: 'Saved views',
                  parent: { label: 'Settings', to: ROUTES.settings },
                },
              },
              {
                path: 'mail',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <MailSettings />),
                handle: { title: 'Email' },
              },
              {
                path: 'api',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <ApiSettings />),
                errorElement: <RouteError />,
                handle: {
                  title: 'API & webhooks',
                  parent: { label: 'Settings', to: ROUTES.settings },
                },
              },
              {
                path: 'audit',
                element: chunk(<SettingsPaneSkeleton measure="wide" />, <AuditSettings />),
                errorElement: <RouteError />,
                handle: { title: 'Audit log', parent: { label: 'Settings', to: ROUTES.settings } },
              },
            ],
          },

          { path: '*', element: <NotFound />, handle: { title: 'Not found' } },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
